/**
 * ShareAvaModal — the "Share my AvA" popup (founder spec, Dec 2026).
 * Preview (clip when available, else portrait) + shareable URL with
 * copy-to-clipboard + social share icons + explicit X close.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, Mail, Facebook, Twitter, MessageCircle, Share2 } from 'lucide-react';

export const ShareAvaModal = ({ open, onClose, avaName, avatarUrl, videoUrl }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const shareUrl = videoUrl || window.location.origin;
  const shareText = `Meet ${avaName || 'my AvA'}, my AvA on HiBFF 💙`;
  const enc = encodeURIComponent;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard unavailable */ }
  };

  const socials = [
    { label: 'WhatsApp', icon: MessageCircle, href: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`, cls: 'bg-green-500 hover:bg-green-600' },
    { label: 'X', icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`, cls: 'bg-gray-900 hover:bg-black' },
    { label: 'Facebook', icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, cls: 'bg-blue-600 hover:bg-blue-700' },
    { label: 'Email', icon: Mail, href: `mailto:?subject=${enc(shareText)}&body=${enc(`${shareText}\n${shareUrl}`)}`, cls: 'bg-amber-500 hover:bg-amber-600' },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[9600] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-ava-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          {videoUrl ? (
            <video src={videoUrl} autoPlay loop playsInline controls className="w-full aspect-square object-cover" data-testid="share-ava-video" />
          ) : (
            <img src={avatarUrl} alt={avaName || 'AvA'} className="w-full aspect-square object-cover" data-testid="share-ava-photo" />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/45 hover:bg-black/65 text-white flex items-center justify-center transition"
            data-testid="share-ava-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <h3 className="text-lg font-bold text-gray-900 text-center">Share {avaName || 'my AvA'}</h3>

          {/* Shareable link + copy */}
          <div className="mt-3 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
            <input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 bg-transparent text-xs text-gray-600 outline-none truncate"
              data-testid="share-ava-link"
            />
            <button
              type="button"
              onClick={copyLink}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${copied ? 'bg-green-100 text-green-700' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
              data-testid="share-ava-copy-btn"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          {/* Social icons */}
          <div className="mt-4 flex items-center justify-center gap-3" data-testid="share-ava-socials">
            {socials.map((s) => {
              const SIcon = s.icon;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Share via ${s.label}`}
                  title={s.label}
                  className={`w-11 h-11 rounded-full text-white flex items-center justify-center transition-colors ${s.cls}`}
                  data-testid={`share-ava-${s.label.toLowerCase()}`}
                >
                  <SIcon size={19} />
                </a>
              );
            })}
          </div>

          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              type="button"
              onClick={() => navigator.share({ title: shareText, text: shareText, url: shareUrl }).catch(() => {})}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-purple-200 text-purple-700 text-sm font-semibold hover:bg-purple-50 transition-colors"
              data-testid="share-ava-native-btn"
            >
              <Share2 size={15} /> More ways to share
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareAvaModal;
